import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';

// NAO usar MeshoptDecoder.useWorkers(): o decodificador monta o worker
// convertendo as proprias funcoes em texto, e o minificador do build renomeia
// essas funcoes. Resultado medido: "workerProcess is not defined" e o carregamento
// dos moveis trava para sempre (so aparece no build de producao, nao no dev).

/**
 * Contorno preto das pecas. Um atributo `visivel` por vertice deixa esconder o
 * contorno de UMA peca sem separar a geometria (ver construirContornos).
 */
function criarMaterialContorno(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    vertexShader: `
      attribute float visivel;
      varying float vVisivel;
      void main() {
        vVisivel = visivel;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: `
      varying float vVisivel;
      void main() {
        if (vVisivel < 0.5) discard;
        gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
      }`,
  });
}

/**
 * Nucleo 3D do viewer (imperativo, sem React) — porta a logica do prototipo
 * "viewer_teste": selecao de pecas (vermelho x cinza translucido), modos de
 * medida (peca/externa/interna), cotas automaticas, raycast, unidades e GLB.
 *
 * A camada React (ViewerPage) cria uma instancia, recebe eventos via callbacks
 * e comanda por metodos. Todo o 3D (cena, sprites, linhas) vive aqui.
 */

export type GlbUnit = 'auto' | 'mm' | 'cm' | 'm' | 'in';

export type ViewerInfo = { kind: 'piece'; id: string; dims: string } | null;

/**
 * Direcao da folga, no mundo do glTF (Y para cima; a planta baixa fica deitada
 * no piso, em Y). X- = esquerda, X+ = direita, Y+ = cima, Y- = baixo,
 * Z+ = frente, Z- = fundo.
 */
export type DirecaoFolga = 'esquerda' | 'direita' | 'cima' | 'baixo' | 'frente' | 'fundo';

/**
 * Folga (distancia) da peca selecionada ate a vizinha mais proxima numa direcao.
 *
 * - `folga_mm` null = nao existe vizinha nessa direcao (nada ali).
 * - `encostada` = a vizinha toca a peca (folga 0): nao ha cota para desenhar,
 *   mas e a informacao mais util de todas ("encosta na lateral esquerda").
 *   Antes dessas encostadas serem puladas, a cota saltava para a peca de TRAS e
 *   mostrava 320 mm onde o marceneiro ve as duas pecas se tocando.
 */
export interface Folga {
  direcao: DirecaoFolga;
  eixo: 'x' | 'y' | 'z';
  sinal: 1 | -1;
  folga_mm: number | null;
  vizinho: string | null;
  encostada: boolean;
}

export interface ViewerCallbacks {
  onPieceList?: (ids: string[]) => void;
  /**
   * Mudou a selecao. `id` e a peca PRINCIPAL (a que alimenta o painel) e `ids`
   * traz todas as ocorrencias destacadas — a mesma peca pode aparecer 9 vezes
   * no movel e o QR da etiqueta nao diz qual delas e, entao destacamos todas.
   */
  onSelected?: (id: string, ids?: string[]) => void;
  onInfo?: (info: ViewerInfo) => void;
  onLoaded?: (label: string) => void;
  onError?: (message: string) => void;
  /** Clique direito curto numa peca: abre menu de contexto na posicao do cursor. */
  onPieceMenu?: (menu: PieceMenu) => void;
  /** As ferragens foram mostradas/escondidas (inclusive sozinhas, ao escolher uma). */
  onFerragens?: (visiveis: boolean) => void;
  /** Baixando ferragens sob demanda: `carregadas` de `total` (total 0 = terminou). */
  onFerragensProgresso?: (carregadas: number, total: number) => void;
  /** Mudou algo no "medir 2 pontos" (bolinha posta, 1o ponto marcado, desfazer...). */
  onMedir?: (estado: EstadoMedir) => void;
  /**
   * As folgas da peca selecionada foram recalculadas (sempre as 6 direcoes; []
   * quando nao ha selecao). E o que permite ao painel listar as distancias com o
   * NOME da vizinha — no 3D o rotulo da cota pode cair fora da tela.
   */
  onFolgas?: (folgas: Folga[]) => void;
  /**
   * Para que lado da TELA vai cada eixo do mundo agora (bussola do medir). So
   * e avisado com o modo medir ligado, e so quando a camera gira de verdade.
   * No tablet nao existe "mexer o mouse para ver o numero mudar": a pessoa
   * precisa saber ANTES de marcar para onde o X/Y/Z da trava aponta.
   */
  onEixosTela?: (eixos: EixosTela) => void;
}

/**
 * Um eixo do mundo visto na tela: [dx, dy, visivel]. (dx, dy) e a direcao
 * normalizada, com x para a DIREITA e y para BAIXO (como no DOM). `visivel` e
 * o comprimento da projecao antes de normalizar (0 = eixo apontando para
 * dentro/fora da tela, 1 = eixo deitado na tela).
 */
export type EixoTela = [number, number, number];
export interface EixosTela { x: EixoTela; y: EixoTela; z: EixoTela }

/**
 * Onde o ima grudou a bolinha. O marceneiro PRECISA saber disso: dois cliques
 * "no olho" que pararam no meio da face dao um numero errado e ninguem percebe.
 */
export type EncaixeTipo = 'canto' | 'aresta' | 'meio' | 'face' | 'planta';

/**
 * Trava de eixo do medir. Com um eixo travado o 2o ponto e projetado na reta do
 * 1o ponto naquele eixo — e assim que se mede a FOLGA entre duas faces
 * paralelas sem o erro de dois cliques "quase" alinhados.
 * No mundo do glTF (Y-up, o plugin converte o Z-up do SketchUp):
 * X = largura, Y = altura, Z = profundidade.
 */
export type EixoMedida = 'livre' | 'x' | 'y' | 'z';

export interface EstadoMedir {
  /** Bolinha fixa: fica onde foi posta e so vira ponto ao apertar "Marcar ponto". */
  fixa: boolean;
  /** Existe bolinha posicionada esperando ser marcada. */
  mira: boolean;
  /** 1o ponto ja marcado, falta o 2o. */
  pendente: boolean;
  /** Distancia do 1o ponto ate a bolinha (texto pronto), ou '' se nao houver. */
  distancia: string;
  /** Medicoes completas na tela. */
  medidas: number;
  /** Onde o ima grudou a bolinha agora ('' = sem bolinha na tela). */
  encaixe: EncaixeTipo | '';
  /** Eixo travado da medicao ('livre' = distancia reta). */
  eixo: EixoMedida;
  /** Componentes ate a bolinha ("X 500 · Z 110"); '' se alinhada ou travada. */
  componentes: string;
}

/** Resultado de uma medicao concluida, em mm. */
export interface MedidaInfo {
  reta: number;
  dx: number;
  dy: number;
  dz: number;
  eixo: EixoMedida;
  /** Texto que foi para o rotulo 3D. */
  texto: string;
}

export interface PieceMenu {
  pieceId: string;
  /** Posicao na tela (px) pra ancorar o menu. */
  x: number;
  y: number;
}

interface MeshEntry {
  mesh: THREE.Mesh;
  /** Trecho desta peca dentro do contorno mesclado do movel. */
  contorno?: { attr: THREE.BufferAttribute; inicio: number; qtd: number; visivel: boolean };
  origMat: THREE.Material | THREE.Material[];
  ghost: THREE.Material;
  ids: Set<string>;
  pieceId: string;
}

type Axis = 'x' | 'y' | 'z';
const AXES: Axis[] = ['x', 'y', 'z'];
const vget = (v: THREE.Vector3, a: Axis): number => (a === 'x' ? v.x : a === 'y' ? v.y : v.z);
const vset = (v: THREE.Vector3, a: Axis, n: number): void => {
  if (a === 'x') v.x = n;
  else if (a === 'y') v.y = n;
  else v.z = n;
};
const clamp = (n: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, n));
// Rascunhos do ima: ele roda a cada movimento do ponteiro e percorre todas as
// arestas da peca — alocar Vector3 no meio do laco vira lixo para o coletor.
const tmpA = new THREE.Vector3();
const tmpB = new THREE.Vector3();
const tmpC = new THREE.Vector3();

// Cor por direcao da folga: [cor da linha (hex num), cor do texto (string)].
// A linha e o rotulo da mesma medida usam a MESMA cor (facil de parear).
const GAP_COLORS: Record<string, [number, string]> = {
  x1: [0xff5252, '#ff5252'], // direita  - vermelho
  'x-1': [0xff9800, '#ff9800'], // esquerda - laranja
  y1: [0xffd166, '#ffd166'], // cima     - amarelo
  'y-1': [0x8be34a, '#8be34a'], // baixo    - verde
  z1: [0x42c5f5, '#42c5f5'], // frente   - azul
  'z-1': [0xe060f0, '#e060f0'], // fundo    - magenta
};

// Cor de cada eixo no medir (bussola, setas 3D e botoes da trava): as MESMAS
// das folgas positivas, para o X vermelho da seta ser o X vermelho da cota.
export const COR_EIXO: Record<Axis, string> = { x: '#ff5252', y: '#ffd166', z: '#42c5f5' };

// Nome da direcao por eixo+sinal (mesma chave do GAP_COLORS). Ver DirecaoFolga.
const DIRECAO_FOLGA: Record<string, DirecaoFolga> = {
  x1: 'direita',
  'x-1': 'esquerda',
  y1: 'cima',
  'y-1': 'baixo',
  z1: 'frente',
  'z-1': 'fundo',
};

// Ordem em que as folgas sao entregues (e listadas no painel): a mesma de quem
// olha o movel de frente — esquerda/direita, cima/baixo, frente/fundo.
const ORDEM_FOLGAS: DirecaoFolga[] = ['esquerda', 'direita', 'cima', 'baixo', 'frente', 'fundo'];

export class MovelViewer {
  private readonly container: HTMLElement;
  private readonly cb: ViewerCallbacks;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly controls: OrbitControls;
  private readonly measures = new THREE.Group();
  // Medicoes livres (2 pontos) — grupo separado: NAO some ao trocar modo nem ao
  // ocultar as cotas automaticas.
  private readonly freeMeasures = new THREE.Group();
  private readonly EDGE_MAT = new THREE.LineBasicMaterial({ color: 0x000000 });
  // Contornos MESCLADOS: uma unica LineSegments por movel. Antes era uma por
  // peca — metade de todas as chamadas de desenho eram so linha preta.
  private readonly contornos = new THREE.Group();
  private readonly MAT_CONTORNO = criarMaterialContorno();
  // Ferragens em .glb SEPARADO (servidor divide madeira/ferragens): o endereco
  // fica guardado aqui e so e baixado ao mostrar. Eram ~98% do download.
  private fontesFerragens: string[] = [];
  private baixandoFerragens: Promise<void> | null = null;
  private ferragensOcultas = false;
  // Renderizar so quando algo muda. Antes a cena era redesenhada 60x por
  // segundo mesmo parada — no tablet isso esquenta e gasta bateria a toa.
  private sujo = true;
  private pointMeasure = false;
  private pendingPoint: THREE.Vector3 | null = null;
  private previewDot: THREE.Mesh | null = null;
  // Bolinha fixa (tablet/celular/caneta): o toque POE a bolinha no ponto (com
  // ima) e ela fica la — o dedo nao cobre o alvo e da para girar/dar zoom para
  // conferir antes de apertar "Marcar ponto". Sem ela, a bolinha segue o mouse.
  private bolinhaFixa = false;
  private mira: THREE.Vector3 | null = null;
  // Arrastando a bolinha: diferenca (px) entre o dedo e a bolinha no toque
  // inicial, para ela nao pular para baixo do dedo.
  private arrastandoMira: { dx: number; dy: number } | null = null;
  // Grupo da medicao ainda sem o 2o ponto (para o "Desfazer").
  private medidaAtual: THREE.Group | null = null;
  // Linha do 1o ponto ate a bolinha, antes de marcar o 2o.
  private linhaGuia: THREE.Line | null = null;
  private ultimoEstadoMedir = '';
  // Tipo do encaixe do ima na bolinha atual (canto/aresta/meio/face/planta).
  private encaixeAtual: EncaixeTipo | '' = '';
  // Ponto CRU do ima (sem a trava de eixo). Guardado separado porque a bolinha
  // mostrada ja sai projetada: trocar de eixo precisa do original de volta.
  private miraBruta: THREE.Vector3 | null = null;
  private eixoMedida: EixoMedida = 'livre';
  // Setas +X/+Y/+Z saindo do ponto-ancora do medir. Grupo PROPRIO: nao pode
  // estar no freeMeasures (Desfazer/Limpar mexem la) nem no measures (as cotas
  // automaticas limpam la). Criadas uma vez e so reposicionadas.
  private readonly setasEixo = new THREE.Group();
  private readonly partesSeta = new Map<Axis, {
    haste: THREE.Mesh; ponta: THREE.Mesh; mat: THREE.MeshBasicMaterial; rotulo: THREE.Sprite;
  }>();
  // Ultima direcao dos eixos avisada a bussola (null = avisar no proximo quadro).
  private ultimosEixosTela: EixosTela | null = null;
  // Ultima medicao concluida, em mm (para a tela e para os testes).
  private ultimaMedidaInfo: MedidaInfo | null = null;
  // Arestas DE VERDADE por geometria (EdgesGeometry a 20 graus), em coordenadas
  // LOCAIS. Cache obrigatorio: o ima roda a cada movimento do ponteiro, e a
  // ferragem do plugin 0.9.3 repete a MESMA geometria em dezenas de nos.
  private readonly cacheArestas = new Map<THREE.BufferGeometry, Float32Array | null>();
  // Anel da bolinha de previa: avisa que o ima pegou ARESTA (e nao a face).
  private previewAnel: THREE.Sprite | null = null;
  // Toque: pinca/2 dedos nao pode virar clique; segurar o dedo abre o menu.
  private readonly ponteiros = new Set<number>();
  private gestoMultiplo = false;
  private toqueLongo: ReturnType<typeof setTimeout> | null = null;
  private menuPorToqueLongo = false;
  private hiddenIds = new Set<string>(); // "ocultar frentes" (toggle da barra)
  private manualHidden = new Set<string>(); // ocultadas 1 a 1 no botao direito
  private readonly raycaster = new THREE.Raycaster();
  private readonly mouse = new THREE.Vector2();
  private readonly loader = new GLTFLoader();
  private readonly resizeObserver: ResizeObserver;

  // flatShading: o .glb de ferragens vem SEM normais (e o que deixa o arquivo
  // leve; o three calcula sombreamento plano sozinho). Sem isto, a ferragem
  // selecionada/fantasma ficaria PRETA — a normal chegaria zerada.
  private readonly RED = new THREE.MeshStandardMaterial({
    color: 0xd62828, emissive: 0x3a0d0d, roughness: 0.55, flatShading: true,
  });

  // Planta baixa (piso da tela de projeto). Fica FORA de `meshes`: nao e peca,
  // nao recebe contorno preto e nao pode ser selecionada. Mas E alvo do raycast
  // no modo "medir 2 pontos" — e justamente pra medir distancia no piso.
  private plantaGrupo: THREE.Group | null = null;
  private plantaMesh: THREE.Mesh | null = null;

  private model: THREE.Object3D | null = null;
  private meshes: MeshEntry[] = [];
  private pieceIds: string[] = [];
  // pieceIds marcados como insumo (extras.supply=true no GLB do PranchaMania).
  // Medidas de folga so cruzam pecas do MESMO tipo (insumo<->insumo, peca<->peca)
  // pra evitar cotar a distancia de uma porta ate o parafuso do gabinete.
  private supplyIds = new Set<string>();
  // Caixa (mundo) de cada peca, calculada UMA vez por carregamento. Sem isso,
  // cada clique refazia setFromObject para toda peca x toda mesh — numa casa
  // inteira (milhares de pecas) isso congelava a aba.
  private readonly boxCache = new Map<string, THREE.Box3>();
  private boxCacheOk = false;
  private modelMaxDim = 1000;
  private selectedId = '';
  // Todas as ocorrencias em destaque (vermelho). `selectedId` e o PRIMEIRO delas:
  // e ele que alimenta o painel de informacao e as cotas de folga, porque cada
  // ocorrencia esta num lugar diferente do movel e nao daria pra cotar as 9.
  private destaque = new Set<string>();
  // Numero do ultimo pedido de selecao (ver selectIds): invalida o pedido que
  // ficou esperando as ferragens baixarem quando outro chega antes.
  private pedidoSelecao = 0;
  // Ultimas folgas avisadas pelo onFolgas. Guardadas so para nao repetir o aviso
  // de "sem folgas" e para o painel nao piscar a cada redesenho.
  private folgasSel: Folga[] = [];
  private unit: GlbUnit = 'auto';
  private measuresVisible = true;
  private rafId = 0;
  private downXY: [number, number] | null = null;
  private downButton = 0; // 0 = esquerdo, 2 = direito
  // Enquanto o usuario nao mexer a camera, o modo "projeto" reenquadra sozinho
  // a cada movel que chega. Depois que ele mexe, para de mexer na camera.
  private userMoved = false;
  // Medicao de desempenho (tela ?debug=1 e testes). Custo zero quando ninguem le.
  private intervalos: number[] = [];
  private tempoRender: number[] = [];
  private ultimoQuadro = 0;
  private quadrosDesenhados = 0;

  constructor(container: HTMLElement, callbacks: ViewerCallbacks = {}) {
    this.container = container;
    this.cb = callbacks;

    this.scene.background = new THREE.Color(0x202428);
    this.scene.add(this.measures);
    this.scene.add(this.freeMeasures);
    this.scene.add(this.contornos);
    this.setasEixo.visible = false;
    this.scene.add(this.setasEixo);

    const w = container.clientWidth || 1;
    const h = container.clientHeight || 1;
    this.camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 1e7);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    // Tela de toque (tablet/celular) ja e densa: desenhar em 2x = 4x mais pixels
    // numa GPU fraca, para uma diferenca que quase nao se ve. Limita em 1,5.
    const toque = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
    const dpr = window.devicePixelRatio || 1;
    this.renderer.setPixelRatio(toque ? Math.min(dpr, 1.5) : Math.min(dpr, 2));
    this.renderer.setSize(w, h);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(this.renderer.domElement);

    // Decodifica geometria comprimida com EXT_meshopt_compression (GLBs leves).
    this.loader.setMeshoptDecoder(MeshoptDecoder);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.addEventListener('start', () => { this.userMoved = true; });
    this.controls.addEventListener('change', () => this.invalidar());

    this.scene.add(new THREE.AmbientLight(0xffffff, 0.75));
    const l1 = new THREE.DirectionalLight(0xffffff, 0.9);
    l1.position.set(1, 2, 1.5);
    this.scene.add(l1);
    const l2 = new THREE.DirectionalLight(0xffffff, 0.4);
    l2.position.set(-1, 1, -1);
    this.scene.add(l2);

    // Captura: roda ANTES do OrbitControls, para poder desligar o giro quando o
    // toque comeca em cima da bolinha fixa (ai o dedo arrasta a bolinha).
    this.renderer.domElement.addEventListener('pointerdown', this.onPointerDown, { capture: true });
    this.renderer.domElement.addEventListener('pointerup', this.onPointerUp);
    this.renderer.domElement.addEventListener('pointercancel', this.onPointerCancel);
    this.renderer.domElement.addEventListener('pointermove', this.onPointerMove);
    // Botao direito oculta peca (clique) mas ainda arrasta o movel (segurar).
    // Sem isso o menu do navegador abriria no clique direito.
    this.renderer.domElement.addEventListener('contextmenu', this.onContextMenu);

    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(container);

    const loop = () => {
      this.rafId = requestAnimationFrame(loop);
      // update() devolve true enquanto a camera se move (inclusive a inercia).
      const mexeu = this.controls.update();
      if (!mexeu && !this.sujo) return;
      this.sujo = false;
      this.desenhar();
    };
    loop();
  }

  /** Pede um novo quadro. Chame sempre que algo visivel mudar. */
  invalidar(): void {
    this.sujo = true;
  }

  private desenhar(): void {
    const t0 = performance.now();
    this.escalarSetasEixo(); // zoom muda a distancia: setas com tamanho fixo na tela
    this.renderer.render(this.scene, this.camera);
    const t1 = performance.now();
    if (this.ultimoQuadro) this.guardar(this.intervalos, t1 - this.ultimoQuadro);
    this.guardar(this.tempoRender, t1 - t0);
    this.ultimoQuadro = t1;
    this.quadrosDesenhados += 1;
    // Aqui e barato: o quadro so e desenhado quando algo muda (camera girou,
    // bolinha andou...), e so interessa com o medir ligado.
    if (this.pointMeasure && this.cb.onEixosTela) this.avisarEixosTela();
  }

  /**
   * Direcao na tela dos eixos +X/+Y/+Z do mundo. So a ROTACAO da camera importa:
   * transformDirection usa a parte 3x3 da matrixWorldInverse (atualizada pelo
   * render) e leva o eixo para o espaco da camera, onde x e a direita e y e para
   * cima — dai o y da tela (DOM, para baixo) ser o -y da camera.
   */
  eixosTela(): EixosTela {
    const m = this.camera.matrixWorldInverse;
    const um = (x: number, y: number, z: number): EixoTela => {
      tmpA.set(x, y, z).transformDirection(m);
      const len = Math.hypot(tmpA.x, tmpA.y);
      // Exatamente perpendicular a tela: sem direcao (a bussola desenha um ponto).
      if (len < 1e-6) return [0, 0, 0];
      return [tmpA.x / len, -tmpA.y / len, Math.min(1, len)];
    };
    return { x: um(1, 0, 0), y: um(0, 1, 0), z: um(0, 0, 1) };
  }

  /** Avisa a bussola so quando a direcao muda de verdade (evita re-render a toa). */
  private avisarEixosTela(): void {
    const e = this.eixosTela();
    const a = this.ultimosEixosTela;
    const TOL = 0.004;
    if (a && AXES.every((k) => e[k].every((n, i) => Math.abs(n - a[k][i]!) < TOL))) return;
    this.ultimosEixosTela = e;
    this.cb.onEixosTela?.(e);
  }

  private guardar(lista: number[], v: number): void {
    lista.push(v);
    if (lista.length > 120) lista.shift();
  }

  /**
   * Numeros do renderizador para diagnostico (tela ?debug=1 e testes).
   * `drawCalls` e o principal custo numa GPU de tablet: cada chamada passa pelo
   * driver. `renderMs` e o tempo de CPU gasto para desenhar um quadro.
   */
  metricas(): {
    drawCalls: number; triangulos: number; linhas: number; geometrias: number; texturas: number;
    pecas: number; pixelRatio: number; quadroMs: number; renderMs: number; fps: number; quadros: number;
    ociosoMs: number;
  } {
    const med = (l: number[]) => {
      if (!l.length) return 0;
      const o = [...l].sort((a, b) => a - b);
      return o[Math.floor(o.length / 2)]!;
    };
    const i = this.renderer.info;
    const quadroMs = med(this.intervalos);
    return {
      drawCalls: i.render.calls,
      triangulos: i.render.triangles,
      linhas: i.render.lines,
      geometrias: i.memory.geometries,
      texturas: i.memory.textures,
      pecas: this.meshes.length,
      pixelRatio: this.renderer.getPixelRatio(),
      quadroMs: +quadroMs.toFixed(1),
      renderMs: +med(this.tempoRender).toFixed(2),
      fps: quadroMs ? Math.round(1000 / quadroMs) : 0,
      quadros: this.quadrosDesenhados,
      // Ha quanto tempo nao desenha: com a tela parada o fps nao significa nada.
      ociosoMs: this.ultimoQuadro ? Math.round(performance.now() - this.ultimoQuadro) : 0,
    };
  }

  /** Quanto da geometria e ferragem (insumo) x peca de madeira. So diagnostico. */
  pesoDaGeometria(): { pecas: { malhas: number; triangulos: number }; ferragens: { malhas: number; triangulos: number } } {
    const r = { pecas: { malhas: 0, triangulos: 0 }, ferragens: { malhas: 0, triangulos: 0 } };
    for (const m of this.meshes) {
      const g = m.mesh.geometry as THREE.BufferGeometry;
      const tri = (g.index ? g.index.count : g.attributes.position.count) / 3;
      const alvo = this.isSupply(m.pieceId) ? r.ferragens : r.pecas;
      alvo.malhas += 1;
      alvo.triangulos += tri;
    }
    return r;
  }

  // ---------- unidades ----------
  private mmScale(): number {
    switch (this.unit) {
      case 'mm': return 1;
      case 'cm': return 10;
      case 'm': return 1000;
      case 'in': return 25.4;
      default: return this.modelMaxDim >= 100 ? 1 : 1000; // auto: grande=mm, pequeno=metros
    }
  }
  private fmt(v: number): string {
    return `${Math.round(v * this.mmScale())} mm`;
  }

  // ---------- indice de pecas ----------
  private indexModel(root: THREE.Object3D, append = false): void {
    if (!append) {
      this.meshes = [];
      this.pieceIds = [];
      this.supplyIds.clear();
      // Geometrias antigas nao voltam: o cache do ima iria segurar todas elas.
      this.cacheArestas.clear();
    }
    this.boxCacheOk = false; // geometria mudou -> recalcula as caixas na proxima
    const seen = new Set<string>(this.pieceIds);
    // Entradas criadas AGORA — so elas ganham contorno (senao duplicaria as
    // arestas das pecas ja indexadas ao carregar o proximo movel).
    const novos: MeshEntry[] = [];

    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const ids = new Set<string>();
      // userData.supply (extras.supply do GLB) vem do PranchaMania e marca
      // insumos (parafusos/dobradicas/correidcas). Pega-se de QUALQUER ancestral.
      let isSupply = false;
      let p: THREE.Object3D | null = o;
      while (p) {
        if (p.name) ids.add(p.name);
        if (p.userData && p.userData.supply === true) isSupply = true;
        p = p.parent;
      }
      const pieceId = [...ids].find((n) => /^P/i.test(n)) || mesh.name || [...ids][0] || '';
      const entrada: MeshEntry = { mesh, origMat: mesh.material, ghost: this.makeGhost(), ids, pieceId };
      this.meshes.push(entrada);
      novos.push(entrada);
      ids.forEach((n) => {
        if (/^P/i.test(n) && !seen.has(n)) { seen.add(n); this.pieceIds.push(n); }
        if (isSupply && /^P/i.test(n)) this.supplyIds.add(n);
      });
      if (isSupply && pieceId) this.supplyIds.add(pieceId);
    });

    // Sem nos "P..." NESTE modelo: usa o pieceId de cada mesh dele. A checagem e
    // por modelo (nao global): no modo projeto, cada movel que chega precisa
    // entrar na lista mesmo que os anteriores ja tenham dado ids.
    const rendeuIds = novos.some((m) => [...m.ids].some((n) => /^P/i.test(n)));
    if (!rendeuIds) {
      novos.forEach((m) => {
        if (m.pieceId && !seen.has(m.pieceId)) { seen.add(m.pieceId); this.pieceIds.push(m.pieceId); }
      });
    }
    this.cb.onPieceList?.(this.pieceIds.slice());
    this.buildEdges(novos);
  }

  // Contorno preto nas arestas de cada peca — deixa os limites bem evidentes.
  private buildEdges(alvos: MeshEntry[] = this.meshes): void {
    // FERRAGEM NAO GANHA CONTORNO. Medido no projeto do Carlos (19 moveis):
    // calcular as arestas das ferragens custava 6,1s dos 7,6s do "Mostrar
    // ferragens", com travadas de ate 865ms, e enchia a cena com 1,25 MILHAO de
    // segmentos de linha (contra 21 mil das pecas). Num parafuso de 5mm o
    // contorno preto nao acrescenta nada — o que ele serve e para separar
    // painel de painel.
    this.construirContornos(alvos.filter((e) => !this.isSupply(e.pieceId)));
  }

  /**
   * Junta o contorno de varias pecas numa geometria so (1 chamada de desenho).
   * As posicoes ja saem em coordenadas do mundo: os moveis nao se movem depois de
   * carregados. Cada peca guarda o seu trecho, para esconder so o contorno dela.
   */
  private construirContornos(entradas: MeshEntry[]): void {
    const partes: Array<{ e: MeshEntry; pos: Float32Array }> = [];
    const v = new THREE.Vector3();
    let total = 0;
    for (const e of entradas) {
      const g = e.mesh.geometry as THREE.BufferGeometry | undefined;
      if (!g?.attributes?.position) continue;
      e.mesh.updateWorldMatrix(true, false);
      const arestas = new THREE.EdgesGeometry(g, 20);
      const src = arestas.attributes.position!.array as Float32Array;
      const out = new Float32Array(src.length);
      for (let i = 0; i < src.length; i += 3) {
        v.set(src[i]!, src[i + 1]!, src[i + 2]!).applyMatrix4(e.mesh.matrixWorld);
        out[i] = v.x; out[i + 1] = v.y; out[i + 2] = v.z;
      }
      arestas.dispose();
      if (out.length) { partes.push({ e, pos: out }); total += out.length; }
    }
    if (!total) return;

    const pos = new Float32Array(total);
    const vis = new Float32Array(total / 3);
    const attr = new THREE.BufferAttribute(vis, 1);
    attr.setUsage(THREE.DynamicDrawUsage);
    let off = 0;
    for (const { e, pos: p } of partes) {
      pos.set(p, off);
      const inicio = off / 3;
      const qtd = p.length / 3;
      const visivel = !this.isHidden(e);
      vis.fill(visivel ? 1 : 0, inicio, inicio + qtd);
      e.contorno = { attr, inicio, qtd, visivel };
      off += p.length;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('visivel', attr);
    geo.computeBoundingSphere();
    const seg = new THREE.LineSegments(geo, this.MAT_CONTORNO);
    seg.renderOrder = 1;
    this.contornos.add(seg);
    this.invalidar();
  }

  private limparContornos(): void {
    for (const c of [...this.contornos.children]) {
      this.contornos.remove(c);
      (c as THREE.LineSegments).geometry.dispose();
    }
  }

  /**
   * Mostra/esconde as ferragens (parafuso, dobradica, corredica). Medido no
   * projeto de 19 moveis: 99,6% dos triangulos eram ferragem (8 milhoes) contra
   * 34 mil das pecas de madeira. Na vista da casa inteira elas nao se veem e so
   * pesam — por isso a tela de projeto comeca com elas ocultas.
   */
  setFerragensVisiveis(visiveis: boolean): Promise<void> {
    if (this.ferragensOcultas !== !visiveis) {
      this.ferragensOcultas = !visiveis;
      this.paint();
      this.cb.onFerragens?.(visiveis);
    }
    return visiveis ? this.baixarFerragensPendentes() : Promise.resolve();
  }

  /** Baixa, um por vez, os .glb de ferragens ainda nao carregados. */
  private baixarFerragensPendentes(): Promise<void> {
    if (this.baixandoFerragens) return this.baixandoFerragens;
    if (!this.fontesFerragens.length) return Promise.resolve();
    const tarefa = (async () => {
      let feitas = 0;
      while (this.fontesFerragens.length) {
        const url = this.fontesFerragens.shift()!;
        this.cb.onFerragensProgresso?.(feitas, feitas + this.fontesFerragens.length + 1);
        try {
          const gltf = await this.loader.loadAsync(url);
          if (!this.model) break; // a cena foi trocada no meio
          this.model.add(gltf.scene);
          this.indexModel(gltf.scene, true); // mesmas coordenadas do mundo: encaixa sozinho
          this.paint();
        } catch {
          // uma ferragem que falhou nao impede as outras
        }
        feitas++;
      }
      this.cb.onFerragensProgresso?.(0, 0);
    })().finally(() => { this.baixandoFerragens = null; });
    this.baixandoFerragens = tarefa;
    return tarefa;
  }

  /** Guarda o endereco das ferragens deste movel; baixa na hora se ja estiverem visiveis. */
  private registrarFerragens(url?: string): void {
    if (!url) return;
    this.fontesFerragens.push(url);
    if (!this.ferragensOcultas) void this.baixarFerragensPendentes();
  }

  ferragensVisiveis(): boolean {
    return !this.ferragensOcultas;
  }

  /** true se o pieceId esta marcado como insumo (extras.supply do GLB). */
  private isSupply(id: string): boolean {
    return this.supplyIds.has(id);
  }

  private makeGhost(): THREE.Material {
    return new THREE.MeshStandardMaterial({
      color: 0x9aa1ab, transparent: true, opacity: 0.16, depthWrite: false, roughness: 0.9,
      flatShading: true, // ver comentario do RED (ferragem vem sem normais)
    });
  }

  // ---------- bounding boxes ----------
  private rebuildBoxCache(): void {
    this.boxCache.clear();
    this.meshes.forEach((m) => {
      const b = new THREE.Box3().setFromObject(m.mesh);
      m.ids.forEach((id) => {
        const atual = this.boxCache.get(id);
        if (atual) atual.union(b);
        else this.boxCache.set(id, b.clone());
      });
    });
    this.boxCacheOk = true;
  }

  private boxOfId(id: string): THREE.Box3 | null {
    if (!this.boxCacheOk) this.rebuildBoxCache();
    const b = this.boxCache.get(id);
    // Clone: quem chama pode expandir/mover a caixa ao desenhar as cotas.
    return b ? b.clone() : null;
  }

  // ---------- materiais ----------
  // Pecas em destaque = vermelhas; demais = cinza translucido. Sem selecao = solido.
  // Percorre `destaque` (no maximo algumas dezenas de ocorrencias) e nao os ids da
  // mesh, para o custo continuar o mesmo de quando era uma peca so.
  private paint(): void {
    this.meshes.forEach((m) => {
      let red = false;
      let ghost = false;
      if (this.destaque.size) {
        for (const id of this.destaque) { if (m.ids.has(id)) { red = true; break; } }
        ghost = !red;
      }
      m.mesh.material = red ? this.RED : ghost ? m.ghost : m.origMat;
      m.mesh.renderOrder = red ? 2 : 0;
      m.mesh.visible = !this.isHidden(m); // respeita "ocultar frentes"
      const c = m.contorno;
      if (c && c.visivel !== m.mesh.visible) {
        (c.attr.array as Float32Array).fill(m.mesh.visible ? 1 : 0, c.inicio, c.inicio + c.qtd);
        c.attr.addUpdateRange(c.inicio, c.qtd);
        c.attr.needsUpdate = true;
        c.visivel = m.mesh.visible;
      }
    });
    this.invalidar();
  }

  private isHidden(m: MeshEntry): boolean {
    if (this.ferragensOcultas && this.isSupply(m.pieceId)) return true;
    if (this.hiddenIds.size === 0 && this.manualHidden.size === 0) return false;
    for (const id of m.ids) {
      if (this.hiddenIds.has(id) || this.manualHidden.has(id)) return true;
    }
    return false;
  }

  /** Oculta (esconde) as pecas cujos ids estao na lista — ex.: frentes/portas. */
  setHiddenPieces(ids: string[]): void {
    this.hiddenIds = new Set(ids);
    this.paint();
  }

  /** Oculta uma peca especifica (usado pelo menu do botao direito). */
  hidePiece(id: string): void {
    if (!id) return;
    this.manualHidden.add(id);
    this.paint();
  }

  /** Volta a mostrar todas as pecas ocultadas 1 a 1 no botao direito. */
  clearManualHidden(): void {
    if (this.manualHidden.size === 0) return;
    this.manualHidden.clear();
    this.paint();
  }

  // ---------- rotulos/linhas 3D ----------
  private makeLabelSprite(text: string, pos: THREE.Vector3, heightWorld?: number, color = '#ffd166'): THREE.Sprite {
    const c = document.createElement('canvas');
    const ctx = c.getContext('2d')!;
    ctx.font = 'bold 44px sans-serif';
    const w = ctx.measureText(text).width + 28;
    c.width = w; c.height = 60;
    ctx.font = 'bold 44px sans-serif';
    ctx.fillStyle = 'rgba(20,20,20,0.9)';
    ctx.fillRect(0, 0, w, 60);
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 14, 32);
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(c), depthTest: false }));
    const s = heightWorld ?? this.modelMaxDim * 0.1;
    sp.scale.set(s * (w / 60), s, 1);
    sp.position.copy(pos);
    return sp;
  }

  private label(text: string, pos: THREE.Vector3, heightWorld?: number, color = '#ffd166'): void {
    this.invalidar();
    if (!this.measuresVisible) return;
    this.measures.add(this.makeLabelSprite(text, pos, heightWorld, color));
  }

  private line(a: THREE.Vector3, b: THREE.Vector3, color = 0xffd166): void {
    this.invalidar();
    if (!this.measuresVisible) return;
    const g = new THREE.BufferGeometry().setFromPoints([a, b]);
    const ln = new THREE.Line(g, new THREE.LineBasicMaterial({ color, depthTest: false }));
    ln.renderOrder = 3;
    this.measures.add(ln);
  }

  private clearMeasures(): void {
    this.invalidar();
    // Sem peca selecionada, as folgas tambem acabaram (clearSelection/reset).
    // Com peca selecionada NAO zera: "Ocultar medidas" limpa o desenho, mas a
    // lista do painel e texto e pode continuar na tela.
    if (!this.selectedId && this.folgasSel.length) this.definirFolgas([]);
    while (this.measures.children.length) {
      const child = this.measures.children[0]!;
      this.measures.remove(child);
      const any = child as THREE.Sprite & THREE.Line;
      any.geometry?.dispose?.();
      const mat = any.material as THREE.Material | undefined;
      mat?.dispose?.();
    }
  }

  // ---------- medida livre (2 pontos) ----------
  /** Liga/desliga o modo de medir 2 pontos. Funciona mesmo com as cotas ativas. */
  setPointMeasureMode(on: boolean): void {
    this.pointMeasure = on;
    // A bussola acabou de montar na tela: avisa a direcao no proximo quadro
    // mesmo que a camera nao tenha mexido desde a ultima vez.
    this.ultimosEixosTela = null;
    this.invalidar();
    // Medicao pela metade (so o 1o ponto) nao fica largada na tela.
    this.descartarMedidaAtual();
    // Sair do modo (botao "Concluir" da barra) zera a trava de eixo; enquanto o
    // modo fica ligado a trava PERMANECE, porque quem mede uma folga em X
    // costuma medir varias seguidas.
    this.eixoMedida = 'livre';
    this.ultimaMedidaInfo = null;
    this.mira = null;
    this.soltarMira();
    this.setPreview(null);
    this.atualizarGuia();
    this.avisarMedir();
  }

  /** Liga/desliga a bolinha fixa (ver `bolinhaFixa`). */
  setBolinhaFixa(on: boolean): void {
    this.bolinhaFixa = on;
    this.mira = null;
    this.soltarMira();
    this.setPreview(null);
    this.atualizarGuia();
    this.avisarMedir();
  }

  /** Transforma a bolinha fixa em ponto da medicao. */
  marcarPonto(): void {
    if (!this.pointMeasure || !this.mira) return;
    // O que vira ponto e o ALVO (com a trava de eixo ja aplicada), nao a
    // bolinha crua — e o mesmo ponto que a linha-guia mostra.
    const alvo = this.alvoGuia();
    if (!alvo) return;
    this.addMeasurePoint(alvo.clone());
    // Fechou a medicao: tira a bolinha de cima do resultado.
    if (!this.pendingPoint) {
      this.mira = null;
      this.setPreview(null);
    }
    this.atualizarGuia();
    this.avisarMedir();
  }

  /**
   * Desfaz UM ponto por vez, do fim para o comeco, ate nao sobrar medida
   * nenhuma. Apertando seguidamente: tira o 2o ponto da ultima medida (a cota
   * some e o 1o ponto fica esperando de novo), depois esse 1o ponto, depois o
   * 2o da medida anterior, e assim por diante.
   */
  desfazerPonto(): void {
    if (this.pendingPoint) {
      // Medida pela metade: o ultimo ponto e o 1o dela.
      this.descartarMedidaAtual();
    } else {
      const ultima = this.freeMeasures.children[this.freeMeasures.children.length - 1];
      if (ultima) {
        const primeiro = ultima.userData.primeiro as THREE.Vector3 | undefined;
        this.freeMeasures.remove(ultima);
        this.descartar(ultima);
        // Tirou o 2o ponto: a medida volta a ficar pela metade, com o 1o ponto
        // no lugar — pronta para marcar outro 2o ponto ou desfazer de novo.
        if (primeiro) this.addMeasurePoint(primeiro.clone());
      }
    }
    this.invalidar();
    this.atualizarGuia();
    this.avisarMedir();
  }

  private descartarMedidaAtual(): void {
    this.pendingPoint = null;
    if (!this.medidaAtual) return;
    this.freeMeasures.remove(this.medidaAtual);
    this.descartar(this.medidaAtual);
    this.medidaAtual = null;
    this.invalidar();
  }

  private descartar(obj: THREE.Object3D): void {
    obj.traverse((o) => {
      const any = o as THREE.Mesh;
      any.geometry?.dispose?.();
      const mats = any.material as THREE.Material | THREE.Material[] | undefined;
      for (const m of Array.isArray(mats) ? mats : mats ? [mats] : []) {
        (m as THREE.SpriteMaterial).map?.dispose();
        m.dispose();
      }
    });
  }

  private estadoMedir(): EstadoMedir {
    const alvo = this.alvoGuia();
    return {
      fixa: this.bolinhaFixa,
      mira: !!this.mira,
      pendente: !!this.pendingPoint,
      distancia: this.pendingPoint && alvo ? this.fmt(this.pendingPoint.distanceTo(alvo)) : '',
      medidas: this.freeMeasures.children.length - (this.medidaAtual ? 1 : 0),
      encaixe: alvo ? this.encaixeAtual : '',
      eixo: this.eixoMedida,
      componentes: this.pendingPoint && alvo ? this.componentes(this.pendingPoint, alvo) : '',
    };
  }

  /**
   * ΔX/ΔY/ΔZ de um trecho, e so quando ajudam: com a medida ja alinhada a um
   * eixo (ou com eixo travado) a reta JA e a folga e repetir o numero so polui.
   * Abaixo de 0,5 mm e ruido de clique.
   */
  private componentes(a: THREE.Vector3, b: THREE.Vector3): string {
    if (this.eixoMedida !== 'livre') return '';
    const k = this.mmScale();
    const usados = AXES.filter((e) => Math.abs(vget(b, e) - vget(a, e)) * k > 0.5);
    if (usados.length < 2) return '';
    return usados
      .map((e) => `${e.toUpperCase()} ${Math.round(Math.abs(vget(b, e) - vget(a, e)) * k)}`)
      .join(' · ');
  }

  /**
   * Trava/solta o eixo da medicao. X = largura, Y = altura, Z = profundidade.
   */
  setEixoMedida(eixo: EixoMedida): void {
    this.eixoMedida = eixo;
    // A bolinha ja posta salta na hora para a reta do eixo (o ponto CRU fica
    // guardado, entao trocar de eixo nao perde o lugar que o ima achou).
    if (this.miraBruta) this.setPreview(this.miraBruta.clone());
    this.atualizarGuia();
    this.avisarMedir();
  }

  /** Ultima medicao concluida, em mm (a tela e os testes leem daqui). */
  ultimaMedida(): MedidaInfo | null {
    return this.ultimaMedidaInfo;
  }

  /**
   * Com um eixo travado, o 2o ponto vale SO naquele eixo: e projetado na reta
   * que passa pelo 1o ponto. E assim que se mede a folga entre duas faces
   * paralelas sem o erro dos dois cliques "quase" alinhados.
   */
  private projetarEixo(p: THREE.Vector3): THREE.Vector3 {
    if (this.eixoMedida === 'livre' || !this.pendingPoint) return p;
    const out = this.pendingPoint.clone();
    vset(out, this.eixoMedida, vget(p, this.eixoMedida));
    return out;
  }

  /** Avisa a tela so quando algo muda (o mouse mexe centenas de vezes). */
  private avisarMedir(): void {
    const e = this.estadoMedir();
    const chave = JSON.stringify(e);
    if (chave === this.ultimoEstadoMedir) return;
    this.ultimoEstadoMedir = chave;
    this.cb.onMedir?.(e);
  }

  private alvoGuia(): THREE.Vector3 | null {
    const bruto = this.bolinhaFixa ? this.mira : (this.previewDot?.visible ? this.miraBruta : null);
    return bruto ? this.projetarEixo(bruto) : null;
  }

  private atualizarGuia(): void {
    // As setas dos eixos mudam nos MESMOS momentos que a linha-guia (bolinha
    // posta/arrastada, ponto marcado/desfeito, trava trocada, modo desligado):
    // chamar daqui garante que nenhum desses caminhos esqueca delas.
    this.atualizarSetasEixo();
    const alvo = this.alvoGuia();
    if (!this.pointMeasure || !this.pendingPoint || !alvo) {
      if (this.linhaGuia?.visible) { this.linhaGuia.visible = false; this.invalidar(); }
      return;
    }
    if (!this.linhaGuia) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(6), 3));
      this.linhaGuia = new THREE.Line(g, new THREE.LineBasicMaterial({
        color: 0x00e5ff, transparent: true, opacity: 0.6, depthTest: false,
      }));
      this.linhaGuia.renderOrder = 7;
      this.linhaGuia.frustumCulled = false;
      this.scene.add(this.linhaGuia);
    }
    const pos = this.linhaGuia.geometry.attributes.position as THREE.BufferAttribute;
    pos.setXYZ(0, this.pendingPoint.x, this.pendingPoint.y, this.pendingPoint.z);
    pos.setXYZ(1, alvo.x, alvo.y, alvo.z);
    pos.needsUpdate = true;
    this.linhaGuia.visible = true;
    this.invalidar();
  }
  isPointMeasureMode(): boolean {
    return this.pointMeasure;
  }

  /**
   * Setas +X/+Y/+Z no ponto-ancora: o 1o ponto marcado (prioridade) ou a
   * bolinha fixa ja posta. No tablet e o unico jeito de saber, ANTES de marcar,
   * para qual lado a trava de eixo vai medir. Sem ancora (medida concluida,
   * modo desligado), somem.
   */
  private atualizarSetasEixo(): void {
    const ancora = this.pointMeasure ? (this.pendingPoint ?? this.mira) : null;
    if (!ancora) {
      if (this.setasEixo.visible) { this.setasEixo.visible = false; this.invalidar(); }
      return;
    }
    if (!this.partesSeta.size) this.criarSetasEixo();
    this.setasEixo.position.copy(ancora);
    this.escalarSetasEixo();
    const travado = this.eixoMedida;
    for (const [eixo, s] of this.partesSeta) {
      // Com trava: a seta do eixo travado em destaque e as outras apagadas.
      // "Livre": as tres iguais.
      const destaque = travado === eixo;
      const apagada = travado !== 'livre' && !destaque;
      s.mat.opacity = apagada ? 0.45 : 1;
      (s.rotulo.material as THREE.SpriteMaterial).opacity = apagada ? 0.45 : 1;
      const rh = destaque ? 0.05 : 0.03;
      const rp = destaque ? 0.13 : 0.1;
      s.haste.scale.set(rh, 0.8, rh);
      s.ponta.scale.set(rp, 0.2, rp);
    }
    this.setasEixo.visible = true;
    this.invalidar();
  }

  /**
   * Tamanho das setas FIXO NA TELA (~80 px), nao em mm: em mm elas sumiam no
   * zoom longe de um armario grande e atravessavam um gabinete pequeno. Depende
   * da distancia da camera ao ponto, por isso e refeito a cada quadro desenhado.
   */
  private escalarSetasEixo(): void {
    if (!this.setasEixo.visible) return;
    const dist = this.camera.position.distanceTo(this.setasEixo.position);
    const alturaMundo = 2 * dist * Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2);
    const mundoPorPx = alturaMundo / Math.max(1, this.renderer.domElement.clientHeight);
    this.setasEixo.scale.setScalar(Math.max(1e-3, 80 * mundoPorPx));
  }

  /**
   * Monta as 3 setas em tamanho 1 (o grupo e escalado depois). Haste e ponta
   * sao malhas (e nao Line) porque linha no WebGL tem sempre 1 px: sem
   * espessura nao daria para destacar o eixo travado.
   */
  private criarSetasEixo(): void {
    for (const eixo of AXES) {
      const mat = new THREE.MeshBasicMaterial({
        color: COR_EIXO[eixo], transparent: true, depthTest: false, depthWrite: false,
      });
      const seta = new THREE.Group();
      seta.name = `seta-${eixo}`;
      // Cilindro e cone do three nascem ao longo de +Y: a haste vai de 0 a 0,8
      // e a ponta de 0,8 a 1. Depois o grupo gira para o eixo certo.
      const haste = new THREE.Mesh(new THREE.CylinderGeometry(1, 1, 1, 10), mat);
      haste.position.y = 0.4;
      const ponta = new THREE.Mesh(new THREE.ConeGeometry(1, 1, 16), mat);
      ponta.position.y = 0.9;
      haste.renderOrder = 10;
      ponta.renderOrder = 10;
      const rotulo = this.makeLabelSprite(eixo.toUpperCase(), new THREE.Vector3(0, 1.28, 0), 0.3, COR_EIXO[eixo]);
      (rotulo.material as THREE.SpriteMaterial).transparent = true;
      rotulo.renderOrder = 11;
      seta.add(haste, ponta, rotulo);
      if (eixo === 'x') seta.rotation.z = -Math.PI / 2;
      else if (eixo === 'z') seta.rotation.x = Math.PI / 2;
      this.setasEixo.add(seta);
      this.partesSeta.set(eixo, { haste, ponta, mat, rotulo });
    }
  }

  /** As setas dos eixos estao na tela? (testes) */
  setasEixoVisiveis(): boolean {
    return this.setasEixo.visible && this.partesSeta.size === 3;
  }

  /** Setas dos eixos: qual esta em destaque e a opacidade de cada (testes). */
  setasEixoEstado(): Record<Axis, { opacidade: number; destaque: boolean }> | null {
    if (!this.setasEixoVisiveis()) return null;
    const r = {} as Record<Axis, { opacidade: number; destaque: boolean }>;
    for (const [eixo, s] of this.partesSeta) {
      r[eixo] = { opacidade: s.mat.opacity, destaque: s.haste.scale.x > 0.04 };
    }
    return r;
  }

  /** Fundo da cena (tema claro/escuro). */
  setBackground(hex: number): void {
    this.invalidar();
    this.scene.background = new THREE.Color(hex);
  }

  private modelCenter(): THREE.Vector3 {
    return this.model
      ? new THREE.Box3().setFromObject(this.model).getCenter(new THREE.Vector3())
      : new THREE.Vector3();
  }

  // Bolinha de preview que segue o cursor no modo medir (mostra onde vai marcar).
  private setPreview(p: THREE.Vector3 | null): void {
    if (!p) {
      this.miraBruta = null;
      this.encaixeAtual = '';
      // Sem isso, cada movimento do mouse fora do modo medir redesenhava a tela.
      if (this.previewDot?.visible) { this.previewDot.visible = false; this.invalidar(); }
      return;
    }
    this.invalidar();
    if (!this.previewDot) {
      this.previewDot = new THREE.Mesh(
        new THREE.SphereGeometry(1, 16, 16),
        new THREE.MeshBasicMaterial({ color: 0x00e5ff, transparent: true, opacity: 0.55, depthTest: false }),
      );
      this.previewDot.renderOrder = 8;
      this.scene.add(this.previewDot);
      this.previewAnel = this.criarAnelPrevia();
      this.previewDot.add(this.previewAnel);
    }
    this.miraBruta = p.clone();
    // SINAL VISUAL do ima: canto = bolinha cheia e maior; aresta/meio = bolinha
    // com anel; face = bolinha translucida. E o que responde, de relance, a
    // pergunta "peguei o canto mesmo?".
    const forte = this.encaixeAtual === 'canto';
    const naAresta = this.encaixeAtual === 'aresta' || this.encaixeAtual === 'meio';
    const mat = this.previewDot.material as THREE.MeshBasicMaterial;
    mat.opacity = forte ? 1 : naAresta ? 0.85 : this.bolinhaFixa ? 0.75 : 0.4;
    // Fixa, a bolinha e o alvo do dedo: um pouco maior.
    const base = Math.max(this.modelMaxDim * (this.bolinhaFixa ? 0.011 : 0.008), 0.6);
    this.previewDot.scale.setScalar(base * (forte ? 1.5 : 1));
    if (this.previewAnel) this.previewAnel.visible = naAresta;
    this.previewDot.position.copy(this.projetarEixo(p));
    this.previewDot.visible = true;
  }

  /**
   * Anel da bolinha (encaixe em aresta). Sprite com textura: sempre de frente
   * para a camera, sem precisar reorientar nada a cada quadro.
   */
  private criarAnelPrevia(): THREE.Sprite {
    const c = document.createElement('canvas');
    c.width = 64; c.height = 64;
    const ctx = c.getContext('2d')!;
    ctx.strokeStyle = '#00e5ff';
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.arc(32, 32, 26, 0, Math.PI * 2);
    ctx.stroke();
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({
      map: new THREE.CanvasTexture(c), transparent: true, depthTest: false,
    }));
    // Filho da bolinha: a escala multiplica a dela.
    sp.scale.setScalar(3.2);
    sp.renderOrder = 9;
    sp.visible = false;
    return sp;
  }
  clearFreeMeasures(): void {
    this.invalidar();
    this.pendingPoint = null;
    this.medidaAtual = null;
    this.ultimaMedidaInfo = null;
    while (this.freeMeasures.children.length) {
      const child = this.freeMeasures.children[0]!;
      this.freeMeasures.remove(child);
      this.descartar(child);
    }
    this.atualizarGuia();
    this.avisarMedir();
  }

  private addMeasurePoint(p: THREE.Vector3): void {
    this.invalidar();
    const r = Math.max(this.modelMaxDim * 0.006, 0.5);
    const dot = new THREE.Mesh(
      new THREE.SphereGeometry(r, 12, 12),
      new THREE.MeshBasicMaterial({ color: 0x00e5ff, depthTest: false }),
    );
    dot.renderOrder = 6;
    dot.position.copy(p);

    // Cada medicao e um grupo (2 pontos + linhas + rotulo): o "Desfazer" tira
    // a ultima inteira.
    if (!this.pendingPoint) {
      const grupo = new THREE.Group();
      grupo.add(dot);
      // Guarda o 1o ponto no proprio grupo: e o que permite ao "Desfazer"
      // voltar UM ponto (tirar so o 2o) em vez da medicao inteira.
      grupo.userData.primeiro = p.clone();
      this.freeMeasures.add(grupo);
      this.medidaAtual = grupo;
      this.pendingPoint = p.clone();
      // No clique com mouse nao ha atualizarGuia aqui: as setas nascem ja.
      this.atualizarSetasEixo();
      this.avisarMedir();
      return;
    }
    const grupo = this.medidaAtual ?? new THREE.Group();
    if (!grupo.parent) this.freeMeasures.add(grupo);
    grupo.add(dot);
    this.medidaAtual = null;
    const a = this.pendingPoint;
    this.pendingPoint = null;
    const cyan = 0x00e5ff;
    const addLine = (u: THREE.Vector3, v: THREE.Vector3) => {
      const ln = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([u, v]),
        new THREE.LineBasicMaterial({ color: cyan, depthTest: false }),
      );
      ln.renderOrder = 5;
      grupo.add(ln);
    };
    // Cota entre os 2 pontos.
    addLine(a, p);
    // Rotulo AFASTADO do modelo, ligado por linha-guia (igual as cotas automaticas).
    const mid = a.clone().add(p).multiplyScalar(0.5);
    const dir = mid.clone().sub(this.modelCenter());
    if (dir.length() < this.modelMaxDim * 0.001) dir.set(0, 1, 0);
    dir.normalize();
    const labelPos = mid.clone().add(dir.multiplyScalar(this.modelMaxDim * 0.14));
    addLine(mid, labelPos);
    // Rotulo: a reta e, quando ajuda, o que ela esconde. Dois cliques quase
    // alinhados dao um numero MAIOR que a folga real — os componentes contam
    // essa historia; com eixo travado, o rotulo diz de qual folga se trata.
    const reta = a.distanceTo(p);
    const comp = this.componentes(a, p);
    const texto = this.eixoMedida !== 'livre'
      ? `${this.fmt(reta)} (${this.eixoMedida.toUpperCase()})`
      : comp ? `${this.fmt(reta)} (${comp})` : this.fmt(reta);
    const k = this.mmScale();
    this.ultimaMedidaInfo = {
      reta: reta * k,
      dx: (p.x - a.x) * k,
      dy: (p.y - a.y) * k,
      dz: (p.z - a.z) * k,
      eixo: this.eixoMedida,
      texto,
    };
    grupo.add(this.makeLabelSprite(texto, labelPos, this.modelMaxDim * 0.06, '#00e5ff'));
    this.atualizarGuia();
    this.avisarMedir();
  }

  // ---------- selecao ----------
  /** Destaca UMA peca (atalho de `selectIds`, que e o caminho de verdade). */
  selectId(id: string): void {
    this.selectIds(id ? [id] : []);
  }

  /**
   * Destaca VARIAS ocorrencias de uma vez. O QR da etiqueta traz o id da
   * ENTIDADE e a mesma prateleira pode aparecer 9 vezes no movel: em vez de
   * escolher uma (e mentir), o site pinta as 9 de vermelho.
   *
   * A PRIMEIRA da lista continua sendo "a selecionada": e dela o painel de
   * informacao, as cotas de folga e o enquadramento da camera.
   */
  selectIds(ids: string[]): void {
    const lista = [...new Set(ids.filter(Boolean))];
    const principal = lista[0] ?? '';
    // Peca que nao esta na cena mas ainda ha ferragens por baixar (escolhida na
    // lista): baixa as ferragens e seleciona quando chegarem.
    if (principal && !this.pieceIds.includes(principal) && (this.fontesFerragens.length || this.baixandoFerragens)) {
      // Quem pediu por ultimo e que manda: se a pessoa clicar em outra peca
      // enquanto as ferragens baixam, este pedido atrasado NAO pode voltar e
      // roubar a selecao (aconteceu: o painel mostrava as folgas do parafuso
      // clicado antes, nao da peca escolhida depois).
      const pedido = ++this.pedidoSelecao;
      void this.setFerragensVisiveis(true).then(() => {
        if (pedido === this.pedidoSelecao && this.pieceIds.includes(principal)) this.selectIds(lista);
      });
      return;
    }
    this.pedidoSelecao++;
    // Escolheu uma ferragem pela lista com elas ocultas: mostra, senao "nada acontece".
    if (principal && this.ferragensOcultas && this.isSupply(principal)) void this.setFerragensVisiveis(true);
    this.destaque = new Set(lista);
    this.selectedId = principal;
    this.paint();
    this.cb.onSelected?.(principal, lista);
    this.showPiece(principal);
    this.drawSelectionMeasures();
  }

  /** Ids atualmente em destaque (o primeiro e o selecionado). */
  getDestaque(): string[] {
    return [...this.destaque];
  }

  /**
   * Ids de peca que estao ESCONDIDAS agora (ocultar frentes, ocultar peca uma a
   * uma, ferragens ocultas). Um id so conta como escondido se NENHUMA malha dele
   * aparece — a mesma malha pode carregar varios nomes (no e pai no GLB).
   */
  private idsOcultos(): Set<string> {
    const ocultos = new Set<string>();
    const visiveis = new Set<string>();
    for (const m of this.meshes) {
      const alvo = this.isHidden(m) ? ocultos : visiveis;
      for (const id of m.ids) alvo.add(id);
    }
    for (const id of visiveis) ocultos.delete(id);
    return ocultos;
  }

  /**
   * Acha, em cada uma das 6 direcoes, a peca vizinha MAIS PROXIMA da selecionada
   * e a folga ate ela. Devolve tambem a caixa da vizinha e o vao em unidades do
   * modelo, porque quem desenha as cotas precisa disso.
   *
   * Regras (todas vindas da reclamacao "nao acho as medidas da peca"):
   * 1. Vizinha = peca que se SOBREPOE nos outros dois eixos (senao a cota
   *    atravessaria o ar ate uma peca de outro modulo).
   * 2. A ENCOSTADA conta e vence: ela e a mais proxima (folga 0). Antes ela era
   *    pulada e a cota ia parar na peca de tras, mostrando um numero grande onde
   *    a pessoa ve as duas pecas se tocando.
   * 3. Peca ESCONDIDA nao entra: medir ate algo que a pessoa mandou sumir da
   *    tela so confunde.
   * 4. So compara peca com peca e insumo com insumo (ver comentario do supply).
   *
   * Caixa envolvente (AABB) alinhada aos eixos do mundo: serve para movel
   * ortogonal, que e o caso do marceneiro. Peca girada em diagonal teria a
   * caixa maior que ela mesma e a folga sairia menor que a real.
   */
  private analisarFolgas(): { itens: Array<{ folga: Folga; box: THREE.Box3 | null; vao: number }>; sel: THREE.Box3; eps: number } | null {
    if (!this.selectedId) return null;
    const sel = this.boxOfId(this.selectedId);
    if (!sel) return null;

    const eps = this.modelMaxDim * 0.001;
    const escondidos = this.idsOcultos();
    const selIsSupply = this.isSupply(this.selectedId);
    const others = this.pieceIds
      .filter((id) => id !== this.selectedId && this.isSupply(id) === selIsSupply && !escondidos.has(id))
      .map((id) => ({ id, box: this.boxOfId(id) }))
      .filter((o): o is { id: string; box: THREE.Box3 } => o.box != null);

    const achados = new Map<DirecaoFolga, { folga: Folga; box: THREE.Box3 | null; vao: number }>();
    for (const A of AXES) {
      const [B, C] = AXES.filter((a) => a !== A) as [Axis, Axis];
      for (const sign of [1, -1] as const) {
        const direcao = DIRECAO_FOLGA[`${A}${sign}`]!;
        let best: { dist: number; vao: number; box: THREE.Box3; id: string } | null = null;
        for (const o of others) {
          // So conta como "vizinha" se houver sobreposicao nos outros dois eixos.
          const overlapB = Math.min(vget(sel.max, B), vget(o.box.max, B)) - Math.max(vget(sel.min, B), vget(o.box.min, B));
          const overlapC = Math.min(vget(sel.max, C), vget(o.box.max, C)) - Math.max(vget(sel.min, C), vget(o.box.min, C));
          if (overlapB <= eps || overlapC <= eps) continue;
          // A vizinha precisa AVANCAR para este lado. Sem isto, uma peca colada
          // do lado ESQUERDO entraria tambem na direita com folga negativa
          // (e, por ser a "menor", venceria) — cota apontando para o lado errado.
          const avanca = sign > 0
            ? vget(o.box.max, A) > vget(sel.max, A) + eps
            : vget(o.box.min, A) < vget(sel.min, A) - eps;
          if (!avanca) continue;
          const vao = sign > 0 ? vget(o.box.min, A) - vget(sel.max, A) : vget(sel.min, A) - vget(o.box.max, A);
          // Vao negativo = as caixas se interpenetram um pouco: para o marceneiro
          // isso e "encostada" (distancia 0), nao um numero negativo.
          const dist = Math.max(vao, 0);
          if (!best || dist < best.dist) best = { dist, vao, box: o.box, id: o.id };
        }
        const encostada = !!best && best.dist <= eps;
        achados.set(direcao, {
          folga: {
            direcao,
            eixo: A,
            sinal: sign,
            folga_mm: best ? (encostada ? 0 : Math.round(best.dist * this.mmScale())) : null,
            vizinho: best ? best.id : null,
            encostada,
          },
          box: best && !encostada ? best.box : null,
          vao: best ? best.vao : 0,
        });
      }
    }
    return { itens: ORDEM_FOLGAS.map((d) => achados.get(d)!), sel, eps };
  }

  /** Guarda e avisa as folgas atuais (ver onFolgas). */
  private definirFolgas(folgas: Folga[]): void {
    this.folgasSel = folgas;
    this.cb.onFolgas?.(folgas);
  }

  /**
   * Folgas da peca selecionada, recalculadas na hora (6 direcoes; [] sem
   * selecao). Recalcula em vez de devolver o guardado para responder certo logo
   * depois de esconder uma peca — usado pelos testes e por quem quiser conferir.
   */
  folgasDaSelecao(): Folga[] {
    const r = this.analisarFolgas();
    return r ? r.itens.map((i) => i.folga) : [];
  }

  /**
   * Desenha as DISTANCIAS (folgas) da peca selecionada ate a peca vizinha mais
   * proxima em cada direcao (±X, ±Y, ±Z). A cota fica DENTRO do vao, entre as duas
   * pecas. Posicoes vem da geometria do GLB (nao precisa o plugin enviar nada).
   *
   * Vizinha ENCOSTADA nao ganha linha nem rotulo: nao existe vao para desenhar
   * e um "0 mm" no 3D so sujaria a tela. Ela aparece na LISTA do painel, que e
   * onde da para ler o nome da vizinha.
   */
  private drawSelectionMeasures(): void {
    this.clearMeasures();
    const analise = this.analisarFolgas();
    if (!analise) { this.definirFolgas([]); return; }
    const { sel, eps } = analise;

    const selSize = sel.getSize(new THREE.Vector3());
    const selMax = Math.max(selSize.x, selSize.y, selSize.z) || this.modelMaxDim;
    const labelH = clamp(selMax * 0.4, this.modelMaxDim * 0.02, this.modelMaxDim * 0.07);
    const center = new THREE.Box3().setFromObject(this.model as THREE.Object3D).getCenter(new THREE.Vector3());

    for (const item of analise.itens) {
      if (!item.box) continue; // sem vizinha, ou encostada (nao ha vao para cotar)
      const A = item.folga.eixo as Axis;
      const sign = item.folga.sinal;
      const [B, C] = AXES.filter((a) => a !== A) as [Axis, Axis];
      const [colNum, colStr] = GAP_COLORS[`${A}${sign}`] ?? [0xffd166, '#ffd166'];

      // Centro da regiao de sobreposicao (a cota fica "no meio" do vao).
      const midB = (Math.max(vget(sel.min, B), vget(item.box.min, B)) + Math.min(vget(sel.max, B), vget(item.box.max, B))) / 2;
      const midC = (Math.max(vget(sel.min, C), vget(item.box.min, C)) + Math.min(vget(sel.max, C), vget(item.box.max, C))) / 2;
      const aStart = sign > 0 ? vget(sel.max, A) : vget(sel.min, A);
      const aEnd = sign > 0 ? vget(item.box.min, A) : vget(item.box.max, A);

      const p1 = new THREE.Vector3();
      const p2 = new THREE.Vector3();
      const mid = new THREE.Vector3();
      for (const [vec, aVal] of [[p1, aStart], [p2, aEnd], [mid, (aStart + aEnd) / 2]] as const) {
        vset(vec, A, aVal);
        vset(vec, B, midB);
        vset(vec, C, midC);
      }

      // A COTA (de onde ate onde esta medindo) fica no vao, entre as duas pecas.
      this.line(p1, p2, colNum);

      // O ROTULO fica AFASTADO (fora do modelo), ligado por uma linha-guia da
      // mesma cor — assim da pra ver a linha da cota e ler o numero sem cobrir.
      const dir = mid.clone().sub(center);
      vset(dir, A, 0); // afasta perpendicular a cota (no plano dos outros eixos)
      if (dir.length() < eps) vset(dir, B, 1);
      dir.normalize();
      const offset = labelH * 2.2 + this.modelMaxDim * 0.02;
      const labelPos = mid.clone().add(dir.multiplyScalar(offset));

      this.line(mid, labelPos, colNum); // linha-guia ate o rotulo
      this.label(this.fmt(item.vao), labelPos, labelH, colStr);
    }

    this.definirFolgas(analise.itens.map((i) => i.folga));
  }

  /** Liga/desliga as cotas (lines+labels) no 3D, mantendo o restante. */
  setMeasuresVisible(visible: boolean): void {
    this.invalidar();
    this.measuresVisible = visible;
    if (!visible) { this.clearMeasures(); return; }
    this.drawSelectionMeasures();
  }

  getMeasuresVisible(): boolean {
    return this.measuresVisible;
  }

  private showPiece(id: string): void {
    const box = this.boxOfId(id);
    if (!box) { this.cb.onInfo?.(null); return; }
    const s = box.getSize(new THREE.Vector3());
    const arr = [s.x, s.y, s.z].sort((a, b) => b - a);
    const dims = `${this.fmt(arr[0]!)} × ${this.fmt(arr[1]!)} × ${this.fmt(arr[2]!)}`;
    this.cb.onInfo?.({ kind: 'piece', id, dims });
  }

  clearSelection(): void {
    this.selectedId = '';
    this.destaque.clear();
    this.clearMeasures();
    this.paint();
    this.cb.onSelected?.('', []);
    this.cb.onInfo?.(null);
  }

  // Zera a selecao (usado ao carregar um novo modelo).
  private resetSelection(): void {
    this.selectedId = '';
    this.destaque.clear();
    this.clearMeasures();
    this.cb.onSelected?.('', []);
    this.paint();
    this.cb.onInfo?.(null);
  }

  // ---------- raycast ----------
  private onPointerDown = (e: PointerEvent): void => {
    this.ponteiros.add(e.pointerId);
    // Segundo dedo (pinca/arrastar com 2 dedos): nada deste gesto e clique.
    if (this.ponteiros.size > 1) {
      this.gestoMultiplo = true;
      this.cancelarToqueLongo();
      this.soltarMira();
      return;
    }
    this.gestoMultiplo = false;
    this.menuPorToqueLongo = false;
    this.downXY = [e.clientX, e.clientY];
    this.downButton = e.button;

    // Toque em cima da bolinha fixa: arrasta a bolinha em vez de girar a cena.
    if (this.pointMeasure && this.bolinhaFixa && this.mira && e.button === 0) {
      // O dedo mira a bolinha que ESTA na tela (com a trava de eixo aplicada).
      const tela = this.naTela(this.alvoGuia() ?? this.mira);
      const raio = e.pointerType === 'mouse' ? 16 : 36;
      if (tela && Math.hypot(tela.x - e.clientX, tela.y - e.clientY) <= raio) {
        this.arrastandoMira = { dx: tela.x - e.clientX, dy: tela.y - e.clientY };
        this.controls.enabled = false; // este listener roda antes do OrbitControls
        return;
      }
    }

    // Sem mouse nao existe botao direito: segurar o dedo na peca abre o menu.
    if (e.pointerType !== 'mouse' && !this.pointMeasure) {
      const [x, y] = this.downXY;
      this.toqueLongo = setTimeout(() => {
        this.toqueLongo = null;
        if (!this.downXY || this.gestoMultiplo) return;
        const m = this.pecaEm(x, y);
        if (!m?.pieceId) return;
        this.menuPorToqueLongo = true;
        this.cb.onPieceMenu?.({ pieceId: m.pieceId, x, y });
      }, 550);
    }
  };

  private cancelarToqueLongo(): void {
    if (this.toqueLongo) clearTimeout(this.toqueLongo);
    this.toqueLongo = null;
  }

  private soltarMira(): void {
    if (!this.arrastandoMira) return;
    this.arrastandoMira = null;
    this.controls.enabled = true;
  }

  private onPointerCancel = (e: PointerEvent): void => {
    this.ponteiros.delete(e.pointerId);
    this.cancelarToqueLongo();
    this.soltarMira();
    this.downXY = null;
    if (this.ponteiros.size === 0) this.gestoMultiplo = false;
  };

  /** Posicao (px da janela) de um ponto 3D; null se estiver atras da camera. */
  private naTela(v: THREE.Vector3): { x: number; y: number } | null {
    const p = v.clone().project(this.camera);
    if (p.z > 1) return null;
    const r = this.renderer.domElement.getBoundingClientRect();
    return { x: r.left + ((p.x + 1) / 2) * r.width, y: r.top + ((1 - p.y) / 2) * r.height };
  }

  private acertoEm(x: number, y: number): THREE.Intersection | undefined {
    const r = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((x - r.left) / r.width) * 2 - 1;
    this.mouse.y = -((y - r.top) / r.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);
    return this.raycaster.intersectObjects(this.alvosRaycast(), false)[0];
  }

  private pecaEm(x: number, y: number): MeshEntry | undefined {
    const hit = this.acertoEm(x, y);
    return hit ? this.meshes.find((m) => m.mesh === hit.object) : undefined;
  }

  // Sempre bloqueia o menu do navegador no canvas (o botao direito serve pra
  // ocultar peca / arrastar o movel, nao pra abrir menu de contexto).
  private onContextMenu = (e: MouseEvent): void => {
    e.preventDefault();
  };

  /**
   * Alvos do raycast. A planta so entra no modo de medir: fora dele, clicar nela
   * nao deve fazer nada (ela nao e peca).
   */
  private alvosRaycast(): THREE.Object3D[] {
    const alvos: THREE.Object3D[] = this.meshes.filter((m) => !this.isHidden(m)).map((m) => m.mesh);
    if (this.pointMeasure && this.plantaMesh?.visible) alvos.push(this.plantaMesh);
    return alvos;
  }

  private onPointerMove = (e: PointerEvent): void => {
    if (this.downXY && Math.hypot(e.clientX - this.downXY[0], e.clientY - this.downXY[1]) > 8) {
      this.cancelarToqueLongo();
    }
    if (this.gestoMultiplo) return;

    if (this.arrastandoMira) {
      const hx = e.clientX + this.arrastandoMira.dx;
      const hy = e.clientY + this.arrastandoMira.dy;
      const hit = this.acertoEm(hx, hy);
      if (hit) {
        this.mira = this.snap(hit, hx, hy, e.pointerType);
        this.setPreview(this.mira);
        this.atualizarGuia();
        this.avisarMedir();
      }
      return;
    }

    if (!this.pointMeasure || !this.model) { this.setPreview(null); return; }
    // Bolinha fixa: fica onde foi posta, nao persegue o ponteiro.
    if (this.bolinhaFixa) return;
    const hit = this.acertoEm(e.clientX, e.clientY);
    this.setPreview(hit ? this.snap(hit, e.clientX, e.clientY, e.pointerType) : null);
    this.atualizarGuia();
    this.avisarMedir();
  };

  // ---------- ima do medir ----------
  // Raio do ima em PIXELS DE TELA (antes era 3% do tamanho do movel, em mm):
  // numa lateral de 2,4 m a face tem 2 triangulos, entao o clique precisava
  // cair a ~7 cm de um canto ou o ponto ficava solto no meio da chapa. Em
  // pixels, quem da zoom ganha precisao — e a aresta, que e o que o marceneiro
  // mais quer, finalmente e alvo.
  private static readonly RAIO_IMA_MOUSE = 14;
  // Dedo/caneta cobrem o alvo: mais folga.
  private static readonly RAIO_IMA_TOQUE = 28;
  // Acima disto (ferragem com milhares de triangulos) nao compensa calcular as
  // arestas da peca inteira: ali o ima usa so o triangulo atingido.
  private static readonly MAX_VERT_IMA = 20000;

  /**
   * Arestas DE VERDADE da geometria, em coordenadas locais (pares de vertices).
   * 20 graus e o MESMO criterio do contorno preto: a diagonal que divide o quad
   * em dois triangulos nao e aresta e nao pode virar alvo do ima.
   */
  private arestasLocais(geo: THREE.BufferGeometry): Float32Array | null {
    const guardado = this.cacheArestas.get(geo);
    if (guardado !== undefined) return guardado;
    let out: Float32Array | null = null;
    if ((geo.attributes.position?.count ?? 0) <= MovelViewer.MAX_VERT_IMA) {
      const a = new THREE.EdgesGeometry(geo, 20);
      out = a.attributes.position!.array as Float32Array;
      a.dispose(); // so libera a GPU; o array em JS continua valendo
    }
    this.cacheArestas.set(geo, out);
    return out;
  }

  /**
   * Ima do "medir 2 pontos". Candidatos, por prioridade:
   * canto (vertice) > meio da aresta > ponto sobre a aresta > ponto cru na face.
   * Tudo comparado em PIXELS de tela (projecao), nao em mm de mundo.
   * Guarda o tipo do encaixe em `encaixeAtual` (a barra e a bolinha mostram).
   */
  private snap(hit: THREE.Intersection, px: number, py: number, pointerType: string): THREE.Vector3 {
    const raw = hit.point.clone();
    // Na planta nao existe ima: o plano tem so 2 triangulos, entao os vertices
    // sao os cantos da imagem inteira e o ponto saltaria metros.
    if (hit.object === this.plantaMesh) { this.encaixeAtual = 'planta'; return raw; }
    this.encaixeAtual = 'face';
    const mesh = hit.object as THREE.Mesh;
    const geo = mesh.geometry as THREE.BufferGeometry | undefined;
    const face = hit.face;
    if (!geo?.attributes.position || !face) return raw;
    const r = this.renderer.domElement.getBoundingClientRect();
    if (!r.width || !r.height) return raw;

    const raioPx = pointerType === 'mouse' ? MovelViewer.RAIO_IMA_MOUSE : MovelViewer.RAIO_IMA_TOQUE;
    // Quantos mm de mundo valem 1 px NA PROFUNDIDADE do ponto clicado.
    const mmPorPx = (2 * Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2)
      * this.camera.position.distanceTo(raw)) / r.height;
    const raioMundo = raioPx * mmPorPx;

    // Plano da face atingida, com a normal virada para a camera. E ele que
    // responde "este candidato esta na superficie que estou vendo ou ATRAS
    // dela?": numa chapa de 18 mm o canto de tras cai quase no mesmo pixel do
    // canto da frente, e sem isso o ima pularia para o lado errado da peca.
    const normal = new THREE.Vector3().copy(face.normal).transformDirection(mesh.matrixWorld).normalize();
    if (normal.dot(tmpA.copy(this.camera.position).sub(raw)) < 0) normal.negate();
    const plano = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, raw);
    // Folga do "esta no plano da face": 0,5 mm num movel de 1 m.
    const tolPlano = Math.max(this.modelMaxDim * 0.0005, 1e-4);

    // Raio da camera que passa pelo pixel do ponteiro. E com ele que se acha o
    // ponto da aresta que esta EMBAIXO do cursor: interpolar na TELA e depois
    // repetir a fracao no espaco nao funciona — em perspectiva, o meio da linha
    // desenhada nao e o meio da aresta de verdade (numa aresta em fuga isso dava
    // mais de 100 px de erro e o ima largava a aresta).
    const raioDir = new THREE.Vector3(
      ((px - r.left) / r.width) * 2 - 1,
      -((py - r.top) / r.height) * 2 + 1,
      0.5,
    ).unproject(this.camera).sub(this.camera.position).normalize();

    const proj = new THREE.Vector3();
    const naTelaRapido = (p: THREE.Vector3): { x: number; y: number } | null => {
      proj.copy(p).project(this.camera);
      if (proj.z > 1) return null;
      return { x: r.left + ((proj.x + 1) / 2) * r.width, y: r.top + ((1 - proj.y) / 2) * r.height };
    };

    let melhorP = raw;
    let melhorPrio = 9;
    let melhorD = Infinity;
    let melhorTipo: EncaixeTipo = 'face';
    const considerar = (p: THREE.Vector3, prio: number, tipo: EncaixeTipo): void => {
      const dPlano = plano.distanceToPoint(p);
      if (dPlano < -tolPlano) return; // escondido atras da face que estou vendo
      // Fora do plano da face, so vale se estiver perto TAMBEM no espaco: assim
      // o ima nao atravessa a peca atras de um canto que na tela parece perto.
      if (Math.abs(dPlano) > tolPlano && p.distanceTo(raw) > raioMundo * 1.5) return;
      const t = naTelaRapido(p);
      if (!t) return;
      const d = Math.hypot(t.x - px, t.y - py);
      if (d > raioPx) return;
      if (prio < melhorPrio || (prio === melhorPrio && d < melhorD)) {
        melhorP = p.clone(); melhorPrio = prio; melhorD = d; melhorTipo = tipo;
      }
    };

    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    const segmento = (ax: number, ay: number, az: number, bx: number, by: number, bz: number): void => {
      // A ferragem do 0.9.3 COMPARTILHA a geometria entre varios nos: o que
      // muda e o matrixWorld da malha ATINGIDA, entao e por ele que passamos.
      a.set(ax, ay, az).applyMatrix4(mesh.matrixWorld);
      b.set(bx, by, bz).applyMatrix4(mesh.matrixWorld);
      // Descarte barato antes de projetar: segmento longe do clique E fora do
      // plano da face nao tem candidato aproveitavel.
      const ab = tmpA.copy(b).sub(a);
      const len2 = ab.lengthSq();
      const t0 = len2 > 0 ? clamp(tmpB.copy(raw).sub(a).dot(ab) / len2, 0, 1) : 0;
      const perto = tmpB.copy(a).addScaledVector(ab, t0).distanceTo(raw) <= raioMundo * 1.5;
      if (!perto
        && (Math.abs(plano.distanceToPoint(a)) > tolPlano || Math.abs(plano.distanceToPoint(b)) > tolPlano)) return;
      considerar(a, 0, 'canto');
      considerar(b, 0, 'canto');
      considerar(a.clone().lerp(b, 0.5), 1, 'meio');
      // Ponto da aresta embaixo do cursor = ponto do segmento mais proximo do
      // raio da camera (raioDir ja esta normalizado, por isso o "e" vale 1).
      if (len2 <= 0) return;
      const rr = tmpC.copy(a).sub(this.camera.position);
      const bb = ab.dot(raioDir);
      const den = len2 - bb * bb;
      const s = den > 1e-9 ? clamp((bb * raioDir.dot(rr) - ab.dot(rr)) / den, 0, 1) : 0;
      considerar(a.clone().addScaledVector(ab, s), 2, 'aresta');
    };

    const arestas = this.arestasLocais(geo);
    const pos = geo.attributes.position;
    if (arestas) {
      for (let i = 0; i + 5 < arestas.length; i += 6) {
        segmento(arestas[i]!, arestas[i + 1]!, arestas[i + 2]!,
          arestas[i + 3]!, arestas[i + 4]!, arestas[i + 5]!);
      }
    } else {
      // Malha grande (ferragem): so o triangulo atingido, como era antes.
      const idx = [face.a, face.b, face.c];
      for (let i = 0; i < 3; i++) {
        const u = idx[i]!;
        const w = idx[(i + 1) % 3]!;
        segmento(pos.getX(u), pos.getY(u), pos.getZ(u), pos.getX(w), pos.getY(w), pos.getZ(w));
      }
    }
    this.encaixeAtual = melhorTipo;
    return melhorP;
  }

  private onPointerUp = (e: PointerEvent): void => {
    this.ponteiros.delete(e.pointerId);
    this.cancelarToqueLongo();
    if (this.arrastandoMira) {
      this.soltarMira();
      this.downXY = null;
      return;
    }
    // O menu ja abriu segurando o dedo: soltar nao pode selecionar a peca.
    if (this.menuPorToqueLongo) {
      this.menuPorToqueLongo = false;
      this.downXY = null;
      return;
    }
    if (this.gestoMultiplo) {
      if (this.ponteiros.size === 0) this.gestoMultiplo = false;
      this.downXY = null;
      return;
    }
    if (!this.downXY) return;
    const moved = Math.hypot(e.clientX - this.downXY[0], e.clientY - this.downXY[1]);
    const btn = this.downButton;
    this.downXY = null;
    // Arrastou (rotacionar/pan pelo OrbitControls) -> nao e clique, ignora.
    // No toque o dedo sempre escorrega um pouco: tolerancia maior.
    if (moved > (e.pointerType === 'mouse' ? 5 : 10) || !this.model) return;
    const hit = this.acertoEm(e.clientX, e.clientY);

    // Clicou no vazio (dentro da cena, fora de qualquer peca): sai do modo
    // "raio X" e devolve a cor normal. NAO reexibe as pecas ocultadas — quem
    // faz as duas coisas e o botao "Tudo solido".
    if (!hit) {
      if (btn === 0 && !this.pointMeasure && this.selectedId) this.clearSelection();
      return;
    }

    // Botao DIREITO (clique curto): abre menu de contexto na peca clicada.
    // O ocultar so acontece quando o usuario clica no botao do menu.
    if (btn === 2) {
      const m = this.meshes.find((x) => x.mesh === hit.object);
      if (m?.pieceId) this.cb.onPieceMenu?.({ pieceId: m.pieceId, x: e.clientX, y: e.clientY });
      return;
    }

    // Modo medir 2 pontos: pega o ponto exato clicado na superficie.
    if (this.pointMeasure) {
      const ponto = this.snap(hit, e.clientX, e.clientY, e.pointerType);
      if (this.bolinhaFixa) {
        // So posiciona a bolinha; vira ponto no botao "Marcar ponto".
        this.mira = ponto;
        this.setPreview(ponto);
        this.atualizarGuia();
        this.avisarMedir();
      } else {
        // Projeta ANTES de marcar: com o eixo travado e a reta do eixo que vale.
        this.setPreview(ponto);
        this.addMeasurePoint(this.projetarEixo(ponto));
      }
      return;
    }
    // Botao esquerdo: seleciona a peca clicada.
    const m = this.meshes.find((x) => x.mesh === hit.object);
    if (m?.pieceId) this.selectId(m.pieceId);
  };

  // ---------- modelo ----------
  resetCamera(): void {
    this.userMoved = false;
    if (this.model) this.frameModel(this.model);
  }

  private frameModel(root: THREE.Object3D): void {
    this.invalidar();
    const box = new THREE.Box3().setFromObject(root);
    if (box.isEmpty()) return;
    const size = box.getSize(new THREE.Vector3());
    const c = box.getCenter(new THREE.Vector3());
    this.modelMaxDim = Math.max(size.x, size.y, size.z) || 1000;

    // Enquadra de verdade: olha perpendicular ao eixo horizontal mais longo
    // (um projeto de 25m de corredor precisa se espalhar na LARGURA da tela, nao
    // apontar pra camera) e calcula a distancia ajustando a caixa projetada ao
    // frustum — vertical E horizontal.
    const fovV = THREE.MathUtils.degToRad(this.camera.fov);
    const fovH = 2 * Math.atan(Math.tan(fovV / 2) * Math.max(this.camera.aspect, 0.1));

    const dir = (size.z > size.x
      ? new THREE.Vector3(1.15, 0.6, 0.85)
      : new THREE.Vector3(0.85, 0.6, 1.15)).normalize();

    // Eixos da camera (dir aponta da cena para a camera).
    const right = new THREE.Vector3().crossVectors(dir, new THREE.Vector3(0, 1, 0)).normalize();
    const camUp = new THREE.Vector3().crossVectors(right, dir).normalize();
    const half = size.clone().multiplyScalar(0.5);
    const extensao = (eixo: THREE.Vector3): number =>
      Math.abs(half.x * eixo.x) + Math.abs(half.y * eixo.y) + Math.abs(half.z * eixo.z);

    const dist =
      Math.max(extensao(right) / Math.tan(fovH / 2), extensao(camUp) / Math.tan(fovV / 2)) * 1.06 +
      extensao(dir);

    this.controls.target.copy(c);
    this.camera.position.copy(c).add(dir.multiplyScalar(dist));
    this.camera.near = Math.max(dist / 2000, 0.01);
    this.camera.far = (dist + size.length()) * 12;
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  private setModel(root: THREE.Object3D, label: string): void {
    this.invalidar();
    if (this.model) this.scene.remove(this.model);
    this.limparContornos();
    this.fontesFerragens = [];
    this.model = root;
    this.scene.add(root);
    this.frameModel(root);
    this.indexModel(root);
    this.clearMeasures();
    this.clearFreeMeasures();
    this.hiddenIds.clear();
    this.manualHidden.clear();
    this.resetSelection();
    this.cb.onLoaded?.(label);
  }

  // ---------- carregar ----------
  loadURL(url: string, label = 'Modelo', ferragensUrl?: string): void {
    this.loader.load(
      url,
      (gltf) => {
        this.setModel(gltf.scene, label);
        this.registrarFerragens(ferragensUrl);
      },
      undefined,
      () => this.cb.onError?.('Nao foi possivel carregar o modelo 3D (.glb).'),
    );
  }

  /**
   * Carrega um .glb ADICIONANDO ao que ja esta na cena, sem apagar o resto.
   * Usado no modo "projeto" (a casa montada): um movel por vez, cada um ja nas
   * coordenadas reais do SketchUp — por isso encaixam sozinhos, sem offset.
   */
  appendURL(url: string, label = 'Modelo', ferragensUrl?: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.loader.load(
        url,
        (gltf) => {
          try {
            this.appendModel(gltf.scene, label);
            this.registrarFerragens(ferragensUrl);
            resolve();
          } catch (e) {
            reject(e instanceof Error ? e : new Error(String(e)));
          }
        },
        undefined,
        () => reject(new Error(`Nao foi possivel carregar o modelo de ${label}.`)),
      );
    });
  }

  private appendModel(root: THREE.Object3D, label: string): void {
    this.invalidar();
    if (!this.model) {
      const container = new THREE.Group();
      this.scene.add(container);
      this.model = container;
    }
    this.model.add(root);
    this.indexModel(root, true); // soma ao indice (nao recria o que ja existe)
    // Aplica o estado atual (ferragens ocultas, pecas escondidas, selecao) nas
    // pecas que acabaram de chegar — sem isso elas nasciam todas visiveis.
    this.paint();
    if (!this.userMoved) this.frameModel(this.model);
    this.cb.onLoaded?.(label);
  }

  // ---------- planta baixa (piso) ----------
  /**
   * Coloca a planta baixa deitada no piso.
   *
   * `centro`, `largura_mm`, `altura_mm` e `rotacao_deg` ja vem no sistema do
   * viewer (glTF Y-up, mm): quando a planta vem do SketchUp, o plugin aplica a
   * MESMA conversao do .glb. NAO converter de novo aqui — converter duas vezes
   * joga a planta pra fora da cena.
   *
   * A planta NAO entra em `this.model` nem em `this.meshes`, entao nao mexe no
   * enquadramento (os moveis e que mandam na camera) e nao vira peca clicavel.
   */
  setPlanta(opts: {
    url: string;
    centro: { x: number; y: number; z: number };
    largura_mm: number;
    altura_mm: number;
    rotacao_deg?: number;
    espelhada?: boolean;
    opacidade?: number;
    visivel?: boolean;
  }): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      new THREE.TextureLoader().load(
        opts.url,
        (tex) => {
          this.clearPlanta();
          tex.colorSpace = THREE.SRGBColorSpace;
          const largura = Math.max(opts.largura_mm, 1);
          const altura = Math.max(opts.altura_mm, 1);
          const mat = new THREE.MeshBasicMaterial({
            map: tex,
            transparent: true,
            opacity: opts.opacidade ?? 0.85,
            side: THREE.DoubleSide,
            // Nao escreve profundidade e desenha primeiro: os moveis sempre
            // aparecem por cima, sem z-fighting com pecas que encostam no chao.
            depthWrite: false,
          });
          const mesh = new THREE.Mesh(new THREE.PlaneGeometry(largura, altura), mat);
          mesh.rotation.x = -Math.PI / 2; // deita no piso
          if (opts.espelhada) mesh.scale.x = -1;
          mesh.renderOrder = -1;

          // O giro em torno do eixo Y fica no grupo: assim nao briga com o
          // tombo de -90 graus do plano.
          const grupo = new THREE.Group();
          grupo.position.set(opts.centro.x, opts.centro.y + 1, opts.centro.z); // +1mm: folga anti z-fighting
          grupo.rotation.y = ((opts.rotacao_deg ?? 0) * Math.PI) / 180;
          grupo.add(mesh);
          grupo.visible = opts.visivel !== false;

          this.scene.add(grupo);
          this.plantaGrupo = grupo;
          this.plantaMesh = mesh;
          this.invalidar();
          resolve();
        },
        undefined,
        () => reject(new Error('Nao foi possivel carregar a imagem da planta.')),
      );
    });
  }

  clearPlanta(): void {
    this.invalidar();
    if (!this.plantaGrupo) return;
    this.scene.remove(this.plantaGrupo);
    const mat = this.plantaMesh?.material as THREE.MeshBasicMaterial | undefined;
    mat?.map?.dispose();
    mat?.dispose();
    this.plantaMesh?.geometry.dispose();
    this.plantaGrupo = null;
    this.plantaMesh = null;
  }

  temPlanta(): boolean {
    return this.plantaGrupo != null;
  }

  setPlantaVisivel(v: boolean): void {
    this.invalidar();
    if (this.plantaGrupo) this.plantaGrupo.visible = v;
  }

  setPlantaOpacidade(n: number): void {
    this.invalidar();
    const mat = this.plantaMesh?.material as THREE.MeshBasicMaterial | undefined;
    if (mat) mat.opacity = Math.min(1, Math.max(0, n));
  }

  /** Reposiciona/redimensiona sem recarregar a imagem (ajuste manual na tela). */
  ajustarPlanta(opts: {
    centro?: { x: number; y: number; z: number };
    largura_mm?: number;
    altura_mm?: number;
    rotacao_deg?: number;
  }): void {
    if (!this.plantaGrupo || !this.plantaMesh) return;
    this.invalidar();
    if (opts.centro) this.plantaGrupo.position.set(opts.centro.x, opts.centro.y + 1, opts.centro.z);
    if (opts.rotacao_deg != null) this.plantaGrupo.rotation.y = (opts.rotacao_deg * Math.PI) / 180;
    if (opts.largura_mm != null || opts.altura_mm != null) {
      const g = this.plantaMesh.geometry as THREE.PlaneGeometry;
      const p = g.parameters;
      this.plantaMesh.geometry = new THREE.PlaneGeometry(
        Math.max(opts.largura_mm ?? p.width, 1),
        Math.max(opts.altura_mm ?? p.height, 1),
      );
      g.dispose();
    }
  }

  /** Esvazia a cena (usado antes de montar um projeto do zero). */
  clearScene(): void {
    this.invalidar();
    this.limparContornos();
    this.fontesFerragens = [];
    if (this.model) {
      this.scene.remove(this.model);
      this.model = null;
    }
    this.meshes = [];
    this.pieceIds = [];
    this.supplyIds.clear();
    this.boxCache.clear();
    this.boxCacheOk = false;
    this.clearMeasures();
    this.clearFreeMeasures();
    this.hiddenIds.clear();
    this.manualHidden.clear();
    this.userMoved = false;
    this.resetSelection();
    this.cb.onPieceList?.([]);
  }

  loadArrayBuffer(buffer: ArrayBuffer, label = 'GLB carregado'): void {
    this.loader.parse(
      buffer,
      '',
      (gltf) => this.setModel(gltf.scene, label),
      () => this.cb.onError?.('Erro ao ler o arquivo GLB.'),
    );
  }

  // ---------- resize / dispose ----------
  private onResize(): void {
    this.invalidar();
    const w = this.container.clientWidth || 1;
    const h = this.container.clientHeight || 1;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  dispose(): void {
    cancelAnimationFrame(this.rafId);
    this.clearPlanta();
    this.resizeObserver.disconnect();
    this.renderer.domElement.removeEventListener('pointerdown', this.onPointerDown, { capture: true });
    this.renderer.domElement.removeEventListener('pointerup', this.onPointerUp);
    this.renderer.domElement.removeEventListener('pointercancel', this.onPointerCancel);
    this.cancelarToqueLongo();
    this.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
    this.renderer.domElement.removeEventListener('contextmenu', this.onContextMenu);
    this.controls.dispose();
    this.clearMeasures();
    this.clearFreeMeasures();
    this.descartar(this.setasEixo);
    this.scene.remove(this.setasEixo);
    this.partesSeta.clear();
    this.cacheArestas.clear();
    this.EDGE_MAT.dispose();
    this.limparContornos();
    this.MAT_CONTORNO.dispose();
    this.renderer.dispose();
    if (this.renderer.domElement.parentNode === this.container) {
      this.container.removeChild(this.renderer.domElement);
    }
  }
}
